import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, CloudDownload, Visibility, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import PaginatedTable from '../components/PaginatedTable';

const ReportsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadReports();
  }, [page, rowsPerPage, searchQuery]);

  const loadReports = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockReports = [
        { id: 1, name: 'Revenue vs Expenses - June 2026', type: 'Financial', generatedDate: '2026-06-30', client: 'All Clients', format: 'PDF' },
        { id: 2, name: 'Cash Flow Analysis - Q2 2026', type: 'Cash Flow', generatedDate: '2026-06-28', client: 'All Clients', format: 'Excel' },
        { id: 3, name: 'Client Profitability Report', type: 'Analysis', generatedDate: '2026-06-25', client: 'All Clients', format: 'PDF' },
        { id: 4, name: 'Tax Summary - June 2026', type: 'Tax', generatedDate: '2026-06-22', client: 'All Clients', format: 'PDF' },
        { id: 5, name: 'Outstanding Invoices Report', type: 'AR/AP', generatedDate: '2026-06-20', client: 'All Clients', format: 'Excel' },
      ];
      setReports(mockReports);
      setTotalCount(mockReports.length);
      setLoading(false);
    }, 500);
  };

  const columns = [
    { id: 'name', label: t('report_name'), minWidth: 280 },
    { id: 'type', label: t('type'), minWidth: 130 },
    { id: 'client', label: t('client'), minWidth: 150 },
    { id: 'generatedDate', label: t('generated_date'), minWidth: 130 },
    { id: 'format', label: t('format'), minWidth: 90 },
    { id: 'actions', label: t('actions'), minWidth: 100, align: 'center' },
  ];

  const getTypeColor = (type) => {
    const colors = {
      Financial: { bg: '#E3F2FD', color: '#1976D2' },
      'Cash Flow': { bg: '#E8F5E9', color: '#4CAF50' },
      Analysis: { bg: '#F3E5F5', color: '#9C27B0' },
      Tax: { bg: '#FFF3E0', color: '#FF9800' },
      'AR/AP': { bg: '#FFEBEE', color: '#F44336' },
    };
    return colors[type] || { bg: '#F5F5F5', color: '#666' };
  };

  const renderRow = (report) => {
    const typeColor = getTypeColor(report.type);
    return (
      <TableRow hover key={report.id}>
        <TableCell sx={{ fontWeight: 500 }}>{report.name}</TableCell>
        <TableCell>
          <Chip
            label={report.type}
            size="small"
            sx={{
              backgroundColor: typeColor.bg,
              color: typeColor.color,
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell>{report.client}</TableCell>
        <TableCell>{report.generatedDate}</TableCell>
        <TableCell>
          <Chip label={report.format} size="small" variant="outlined" />
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" color="primary">
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary">
            <CloudDownload fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('accountant_reports')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('generate_financial_reports')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {t('generate_report')}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_reports_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <PaginatedTable
          columns={columns}
          data={reports}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('no_reports_found')}
          renderRow={renderRow}
        />
      </Box>
    </AccountantLayout>
  );
};

export default ReportsPage;
